import {memo, useState} from 'react';
import PropTypes from 'prop-types';
import {groupShape, checklistShape} from '../shapes';

const DeleteIcon = () => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polyline points="1,3 11,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const DotsIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="2.5" r="1" fill="currentColor"/>
        <circle cx="6" cy="6" r="1" fill="currentColor"/>
        <circle cx="6" cy="9.5" r="1" fill="currentColor"/>
    </svg>
);

const EditIcon = () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.5,1.5 L10.5,3.5 L4,10 L1.5,10.5 L2,8 Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="7" y1="3" x2="9" y2="5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
);

const ChevronIcon = ({direction}) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        {direction === 'left'
            ? <polyline points="8,2 4,6 8,10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            : <polyline points="4,2 8,6 4,10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        }
    </svg>
);


const GroupSidebar = memo(function GroupSidebar({groups, selectedGroupId, checklistData, onSelect, onAddGroup, onRenameGroup, onDeleteGroup}) {
    const [open, setOpen] = useState(true);
    const [addingGroup, setAddingGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingDraft, setEditingDraft] = useState('');

    function startEditing(g, e) {
        e.stopPropagation();
        setEditingDraft(g.name);
        setEditingId(g.id);
    }

    function commitRename(id) {
        const name = editingDraft.trim();
        if (name) onRenameGroup(id, name);
        setEditingId(null);
    }

    function handleRenameKeyDown(e, id) {
        if (e.key === 'Enter') commitRename(id);
        if (e.key === 'Escape') setEditingId(null);
    }

    function isGroupEmpty(groupId) {
        if (groupId === null) {
            return !checklistData.some(c => c.groupId == null);
        }
        return !checklistData.some(c => c.groupId === groupId);
    }

    function handleAddConfirm() {
        const name = newGroupName.trim();
        if (!name) {
            setAddingGroup(false);
            return;
        }
        onAddGroup(name);
        setNewGroupName('');
        setAddingGroup(false);
    }

    function handleAddKeyDown(e) {
        if (e.key === 'Enter') handleAddConfirm();
        if (e.key === 'Escape') {
            setNewGroupName('');
            setAddingGroup(false);
        }
    }

    return (
        <div className={`group-sidebar${open ? '' : ' group-sidebar--collapsed'}`}>
            <div className="group-sidebar-header">
                {open && <span className="group-sidebar-label">Groups</span>}
                <button
                    className="group-sidebar-toggle"
                    onClick={() => setOpen(o => !o)}
                    aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                    <ChevronIcon direction={open ? 'left' : 'right'} />
                </button>
            </div>

            {open && (
                <>
                    <div
                        className={`group-sidebar-item${selectedGroupId === 'all' ? ' group-sidebar-item--active' : ''}`}
                        onClick={() => onSelect('all')}
                    >
                        <span className="group-sidebar-item-name">All</span>
                    </div>

                    {groups.length > 0 && (
                        <>
                            <div className="group-sidebar-divider" />
                            <div
                                className={`group-sidebar-item${selectedGroupId === null ? ' group-sidebar-item--active' : ''}`}
                                onClick={() => onSelect(null)}
                            >
                                <span className="group-sidebar-item-name">Default</span>
                            </div>
                        </>
                    )}

                    {groups.map(g => (
                        <div
                            key={g.id}
                            className={`group-sidebar-item${selectedGroupId === g.id ? ' group-sidebar-item--active' : ''}`}
                            onClick={() => editingId !== g.id && onSelect(g.id)}
                        >
                            {editingId === g.id ? (
                                <input
                                    className="group-sidebar-rename-input"
                                    value={editingDraft}
                                    onChange={e => setEditingDraft(e.target.value)}
                                    onBlur={() => commitRename(g.id)}
                                    onKeyDown={e => handleRenameKeyDown(e, g.id)}
                                    onClick={e => e.stopPropagation()}
                                    autoFocus
                                />
                            ) : (
                                <>
                                    <span className="group-sidebar-item-name">{g.name}</span>
                                    <button
                                        className="group-sidebar-action"
                                        onClick={e => startEditing(g, e)}
                                        aria-label={`Rename group ${g.name}`}
                                    >
                                        <EditIcon />
                                    </button>
                                </>
                            )}
                            {editingId !== g.id && isGroupEmpty(g.id) && (
                                <button
                                    className="group-sidebar-delete"
                                    onClick={e => { e.stopPropagation(); onDeleteGroup(g.id); }}
                                    aria-label={`Delete group ${g.name}`}
                                >
                                    <DeleteIcon />
                                </button>
                            )}
                            {editingId !== g.id && (
                                <button
                                    className="group-sidebar-action"
                                    onClick={e => e.stopPropagation()}
                                    aria-label="More options"
                                >
                                    <DotsIcon />
                                </button>
                            )}
                        </div>
                    ))}

                    <div className="group-sidebar-divider" />

                    {addingGroup ? (
                        <div className="group-sidebar-add-form">
                            <input
                                autoFocus
                                value={newGroupName}
                                onChange={e => setNewGroupName(e.target.value)}
                                onKeyDown={handleAddKeyDown}
                                onBlur={handleAddConfirm}
                                placeholder="Group name"
                            />
                        </div>
                    ) : (
                        <button className="group-sidebar-add" onClick={() => setAddingGroup(true)}>
                            + New Group
                        </button>
                    )}
                </>
            )}
        </div>
    );
});

GroupSidebar.propTypes = {
    groups: PropTypes.arrayOf(groupShape).isRequired,
    selectedGroupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    checklistData: PropTypes.arrayOf(checklistShape).isRequired,
    onSelect: PropTypes.func.isRequired,
    onAddGroup: PropTypes.func.isRequired,
    onRenameGroup: PropTypes.func.isRequired,
    onDeleteGroup: PropTypes.func.isRequired,
};

export default GroupSidebar;
